import { Injectable } from '@angular/core';
import { isBoolean } from 'lodash';
import { UtilityService } from 'src/app/my-sedgwick/services/utility.service';
import { TranslationRequest } from '../models/translation/translation-request';
import { TranslationService } from 'src/app/my-sedgwick/services/translation.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CsvService {
  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });
  constructor(private utility: UtilityService,
    private translationService: TranslationService) {}
  async download(csvParams: CsvParams) {
    const rows = csvParams.rows;

    if (!rows || !rows.length) {
      return;
    }

    // Use current language preference so export translates correctly after language change
    this.translationRequest.language = sessionStorage.getItem('languagepreference');

    // Collect all translation promises: headers, footer, and row values
    const allTranslationPromises: Promise<any>[] = [];

    // Header translation promises
    const headersToTranslate = csvParams.headers;
    const headerPromises = headersToTranslate.map(header => 
      firstValueFrom(
        this.translationService.getTranslationForKey$(header, this.translationRequest)
      )
    );
    allTranslationPromises.push(...headerPromises);

    // Footer translation promise
    const footerPromise = firstValueFrom(
      this.translationService.getTranslationForKey$(csvParams.footerNote, this.translationRequest)
    );
    allTranslationPromises.push(footerPromise);

    // FileName translation promise
    const fileNamePromise = firstValueFrom(
      this.translationService.getTranslationForKey$(csvParams.fileName, this.translationRequest)
    );
    allTranslationPromises.push(fileNamePromise);

    // Row value translation promises for specific keys: status, subStatus, claimDescription
    const rowTranslationData: Array<{rowIndex: number, key: string, promise: Promise<string>}> = [];
    
    rows.forEach((row: any, rowIndex: number) => {
      csvParams.keysToTranslate?.forEach(key => {
        if (row[key] != null && row[key] !== undefined && row[key] !== '') {
          const translationPromise = firstValueFrom(
            this.translationService.getTranslationForKey$(row[key], this.translationRequest)
          ).catch(() => row[key]); // If translation fails, keep original value
          rowTranslationData.push({ rowIndex, key, promise: translationPromise });
          allTranslationPromises.push(translationPromise);
        }
      });
    });

    // Wait for all translations to complete
    await Promise.all(allTranslationPromises);

    // Reconstruct translated headers (promises are already resolved)
    csvParams.headers = await Promise.all(headerPromises);

    // Reconstruct translated fileName (promise is already resolved)
    csvParams.fileName = (await fileNamePromise ?? csvParams.fileName) + (csvParams.fileNameSuffix ?? '');

    // Reconstruct translated footer (promise is already resolved)
    csvParams.footerNote = await footerPromise ?? '';

    // Reconstruct translated rows (promises are already resolved)
    const translatedRows = rows.map((row: any) => ({ ...row }));
    for (const { rowIndex, key, promise } of rowTranslationData) {
      try {
        const translatedValue = await promise;
        translatedRows[rowIndex][key] = translatedValue;
      } catch (error) {
        // If translation fails, keep original value (no action needed)
      }
    }

    const separator = ',';
    var headerData = '';
    if (
      csvParams.headerNote != undefined &&
      csvParams.headerNote != null &&
      csvParams.headerNote.length > 1
    ) {
      for (var k = 0; k < csvParams.headerNote.length; k++) {
        headerData = headerData + csvParams.headerNote[k] + '\n';
      }

      for (var t = 0; t < csvParams.headerNoteBefore; t++) {
        headerData = headerData + '\n';
      }
    }

    let csvContent =
      csvParams.headers.join(separator) +
      '\n' +
      translatedRows
        .map((row: any) => {
          return csvParams.keys
            .map((k, keyIndex) => {
              let cell = row[k] === null || row[k] === undefined ? '' : row[k];
              /*cell =
                cell instanceof Date
                  ? cell.toLocaleString():
                  isBoolean(cell)
                  ? cell == true ? 'Yes' : 'No':
                  cell instanceof Object ?
                  row[k].street1? row[k].street1 : '' + " "  + row[k].street2? row[k].street2 : '' +  row[k].city? row[k].city : '' +  row[k].state? row[k].state : ''+  row[k].zipCode? row[k].zipCode : ''
                  : cell.toString().replace(/"/g, '""');*/
              const isDateColumn = csvParams.dataType?.[keyIndex] === 'date';
              if (
                isNaN(cell) &&
                !isNaN(Date.parse(cell)) &&
                !cell.includes('Restriction') // "Restriction n" parces as a valid date but we dont want these as date
                && ((csvParams.includeTimeWithDates === false && isDateColumn)
                || csvParams.includeTimeWithDates === true)
              ) {
                  if (csvParams.includeTimeWithDates === true) {
                    cell = new Date(cell).toLocaleTimeString([], {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                  } else {
                    cell = new Date(cell).toLocaleDateString();
                  }
              } else if (isBoolean(cell)) {
                cell = cell == true ? 'Yes' : 'No';
              } else if (k.includes('.')) {
                let objList = k.split('.');
                cell = row[objList[0]][objList[1]];
              } else {
                cell = cell.toString().replace(/"/g, '""');
                //cell = "\'" + cell;
              }
              if (cell == undefined || cell == null) {
                cell = '';
              } else if (cell.search(/("|,|\n)/g) >= 0) {
                cell = `"${cell}"`;
              } else if (!isNaN(cell)) {
                cell = '="' + cell + '"';
                //cell = '"' + cell + '"';
              }

              return cell;
            })
            .join(separator);
        })
        .join('\n');

    if (
      csvParams.footerNote != undefined &&
      csvParams.footerNote != null &&
      csvParams.footerNote.length > 1
    ) {
      for (var i = 0; i < csvParams.footerAfterRows; i++) {
        for (var j = 0; j < csvParams.headers.length; j++) {
          csvContent = csvContent + separator;
        }
        csvContent = csvContent + '\n';
      }
      const footerLines = csvParams.footerNote.toString().split('\n');
      footerLines.forEach((line, lineIndex) => {
        let footerLine = line.replace(/"/g, '""');
        if (footerLine.search(/("|,)/g) >= 0) {
          footerLine = `"${footerLine}"`;
        }
        csvContent = csvContent + footerLine;
        for (var j = 1; j < csvParams.headers.length; j++) {
          csvContent = csvContent + separator;
        }
        if (lineIndex < footerLines.length - 1) {
          csvContent = csvContent + '\n';
        }
      });
    }

    if (
      csvParams.headerNote != undefined &&
      csvParams.headerNote != null &&
      csvParams.headerNote.length > 1
    ) {
      csvContent = headerData + csvContent;
    }

    // Add UTF-8 BOM to ensure proper encoding recognition by Excel and other programs
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    this.utility.downloadFile(blob, csvParams.fileName);
  }
}

export class CsvParams {
  /**
   * Property names on rowData objects. Keys are written in order to csv.
   */
  keys: string[];
  /**
   * Header labels for keys. Labels are written in order to csv.
   */
  headers: string[];
  /**
   * Array of data types for keys (same order as keys). Optional; used when includeTimeWithDates is false to identify date columns.
   */
  dataType?: string[];
  /**
   * Array of objects to be written to csv file
   */
  rows: any[];
  /**
   * The csv file name
   */
  fileName: string = 'export.csv';
  /**
   * Indicator to include time with Date values
   */
  includeTimeWithDates: boolean = false;

  footerAfterRows: number = 5;

  footerNote: string = '';

  headerNote: any[];
  headerNoteBefore: number = 3;
  
  fileNameSuffix?: string;
  keysToTranslate?: string[];
}
